"use client";

import { InfoToolCard } from "@farm-junction/design-library";
import type { ComponentProps } from "react";
import { useEffect, useState } from "react";
import Image from "next/image";
import Heading from "./ui/Heading";
import { getDealers, getDealersBrands, getDealersCities } from "@/app/utilities/apis";
import { dealerBrandMap, mapDealerCity } from "@/app/utilities/helper";
import { CarouselViewAllFooter, CarouselViewAllHeader } from "./ui/CarouselViewAll";

type IconName = NonNullable<ComponentProps<typeof InfoToolCard>["iconName"]>;

interface Tool {
    testId: string;
    title: string;
    description: string;
    href?: string;
    src?: string;
    iconName?: IconName;
    count?: number | string;
}

type LoadMoreApiType = "dealer-brands" | "dealer-cities" | "dealers" | "";

interface CarBuyingToolsProps {
    title?: string;
    tools: Tool[];
    cardClassName?: string;
    headingClassName?: string;
    descriptionClassName?: string;
    /** Use `{title}` / `{description}` / `{count}` as placeholders, e.g. `{count} Tata Car Dealers in {description}` */
    linkTitle?: string;
    linkAlt?: string;
    /** When true, shows tool.src as image. When false, shows iconName (if provided) or neither. */
    showImage?: boolean;
    /** Hides the trailing chevron icon on each card. */
    hideChevron?: boolean;
    /** When true, shows Load More. Requires initialVisibleCount for local-only lists. */
    showLoadMore?: boolean;
    /** How many cards to show before Load More. Must be passed when showLoadMore is true. */
    loadMoreLabel?: string;
    loadMoreFilters?: any;
    loadMoreApiType?: LoadMoreApiType;
    totalPageCount?: number;
    initialPage?: number;
    dataTestid?: string;
    imageProps?: {
        width?: number;
        height?: number;
        className?: string;
    };
    brandName?: string;
    brandSlug?: string;
    citySlug?: string;
    viewAllLabel?: string;
    viewAllHref?: string;
}

const CarBuyingTools = ({
    title = "Car Buying Tools",
    tools,
    cardClassName,
    headingClassName,
    descriptionClassName,
    showImage = true,
    hideChevron = false,
    showLoadMore = false,
    loadMoreLabel = "Load More",
    loadMoreFilters,
    loadMoreApiType = "",
    totalPageCount,
    initialPage = 1,
    imageProps,
    dataTestid = "",
    brandName = "",
    brandSlug = "",
    citySlug = "",
    viewAllLabel = "",
    viewAllHref = "",
    linkTitle = "",
    linkAlt = "",
}: CarBuyingToolsProps) => {
    const [visibleTools, setVisibleTools] = useState<Tool[]>(tools);
    const [currentPage, setCurrentPage] = useState(initialPage);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    useEffect(() => {
        setVisibleTools(tools);
        setCurrentPage(initialPage);
    }, [initialPage, showLoadMore, tools]);

    const hasLoadedAllPages = typeof totalPageCount === "number" && currentPage >= totalPageCount;
    const hasApiMore = Boolean(loadMoreApiType && !hasLoadedAllPages);
    const hasMore = showLoadMore && hasApiMore;

    const fetchLoadMoreData = async (filters: Record<string, unknown>) => {
        switch (loadMoreApiType) {
            case "dealer-brands":
                return getDealersBrands(filters);

            case "dealer-cities":
                return getDealersCities(filters);

            case "dealers":
                return getDealers(filters);

            default:
                throw new Error(`Unsupported API type: ${loadMoreApiType}`);
        }
    };

    const formatLinkLabel = (template: string, tool: Tool) =>
        template
            .replaceAll("{title}", tool.title ?? "")
            .replaceAll("{description}", tool.description ?? "")
            .replaceAll("{count}", String(tool.count ?? ""));

    const getLinkTitle = (tool: Tool) =>
        linkTitle ? formatLinkLabel(linkTitle, tool) : tool.title;

    const getLinkAlt = (tool: Tool) =>
        linkAlt ? formatLinkLabel(linkAlt, tool) : getLinkTitle(tool);

    const mapLoadMoreData = (data : any) => {
        switch (loadMoreApiType) {
            case "dealer-brands":
                return dealerBrandMap(data, citySlug);

            case "dealer-cities":
                return mapDealerCity(data, brandName, brandSlug);

            // case "dealers":
            //     return getDealers(filters);
        }
    };

    const handleLoadMore = async () => {
        if (isLoadingMore) return;

        if (!loadMoreApiType) {
            setVisibleTools(tools);
            return;
        }

        const nextPage = currentPage + 1;

        try {
            setIsLoadingMore(true);
            const response = await fetchLoadMoreData({
                ...(loadMoreFilters ?? {}),
                page: nextPage,
            });
            const nextItems = Array.isArray(response?.data) ? response.data : [];
            const nextTools = nextItems.map(mapLoadMoreData);

            setVisibleTools((previousTools) => [...previousTools, ...nextTools]);
            setCurrentPage(nextPage);
        } finally {
            setIsLoadingMore(false);
        }
    };

    return (
        <div data-testid={dataTestid.toLowerCase().replace(/\s+/g, "-")}>
            <div className="flex items-center justify-between">
                <Heading tag="h2" className="mb-4 text-2xl font-bold md:text-3xl">
                    {title}
                </Heading>
                {viewAllLabel && viewAllHref && (
                    <CarouselViewAllHeader
                        href={viewAllHref}
                        title={viewAllLabel}
                        className={'mb-4'}
                    />
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {visibleTools.map((tool) => (
                    <InfoToolCard
                        key={tool.testId}
                        className={`border border-gray-200 rounded-[16px]  ${hideChevron ? "[&>a>svg]:hidden" : ""} ${cardClassName ?? ""}`}
                        data-testid={tool.testId}
                        title={tool.title}
                        description={tool.description}
                        href={tool.href ?? "#"}
                        showImage={showImage}
                        {...(showImage ? { src: tool.src } : tool.iconName ? { iconName: tool.iconName } : {})}
                        slotProps={{
                            descriptionProps: {
                                className: `text-[14px] text-[#64748B] font-normal truncate ${descriptionClassName ?? ""}`,
                                isTruncated: false,
                                variant: "p",
                            },
                            headingProps: {
                                className: `text-[16px] text-[#373737] font-semibold truncate ${headingClassName ?? ""}`,
                                level: "p",
                                variant: "p",
                            },
                            linkProps:{
                                className:"px-[16px] py-[16px] md:px-[24px] md:py-[24px]",
                                title: getLinkTitle(tool),
                            },
                            imageProps: {
                                ...imageProps,
                                alt: getLinkAlt(tool),
                            },
                        }}
                    />
                ))}
            </div>
            {viewAllLabel && viewAllHref && (
                <CarouselViewAllFooter href={viewAllHref} title={viewAllLabel} />
            )}

            {hasMore && (
                <button
                    type="button"
                    onClick={handleLoadMore}
                    disabled={isLoadingMore}
                    className="mt-4 flex w-full cursor-pointer items-center justify-center gap-2 rounded-[12px] border border-[#D8DEE8] bg-white px-4 py-3 text-[14px] font-medium text-[#363940] transition hover:bg-gray-50"
                    data-testid="car-buying-tools-load-more"
                >
                    {isLoadingMore ? "Loading..." : loadMoreLabel}
                    <Image
                        src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}view-all-arrow.svg`}
                        alt=""
                        width={20}
                        height={14}
                        className="rotate-90 invert"
                    />
                </button>
            )}
        </div>
    );
};

export default CarBuyingTools;
